import React, { useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { env } from '@/config/env';

import { PageLoader } from '@/elements/PageLoader';

import { SearchParamsEnum } from '@/enums/searchParams.enum';

import useGoogleAnalytics from '@/hooks/GoogleAnalytics/useGoogleAnalytics';
import { useAppNav } from '@/hooks/useAppNav.hook';
import { useAuth } from '@/hooks/useAuth.hook';
import { useSettings } from '@/hooks/useSettings';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { linkToGZ } = useAppNav();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const { login, loginIsLoading, jwt, setJWT, removeJWT } = useAuth();
  const { setLang } = useSettings();

  useGoogleAnalytics();

  const token = useMemo(
    () => searchParams.get(SearchParamsEnum.token),
    [searchParams],
  );
  const lang = useMemo(
    () => searchParams.get(SearchParamsEnum.lang),
    [searchParams],
  );

  useEffect(() => {
    if (!env.FEATURE_NON_LOGIN_ENABLED && !token && !jwt) {
      window.location.assign(linkToGZ);
    }
  }, [jwt, token, linkToGZ]);

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang, () => setLang(lang));
      searchParams.delete(SearchParamsEnum.lang);

      navigate(
        {
          search: searchParams.toString(),
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line
  }, [i18n, lang, navigate, searchParams, setLang, setSearchParams]);

  useEffect(() => {
    if (token) {
      removeJWT();

      (async () => {
        // Do not make a request if the token is logout
        if (token === 'logout') return;

        try {
          const res = await login({ token: token as string }).unwrap();
          setJWT(res.token);
          searchParams.delete(SearchParamsEnum.token);

          navigate(
            {
              search: searchParams.toString(),
            },
            { replace: true },
          );
        } catch (e) {
          Promise.reject(e);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loginIsLoading) {
    return <PageLoader />;
  }

  return <>{children}</>;
};
